import { Helmet } from 'react-helmet-async'
import HomeTemplate from '../templates/HomeTemplate.jsx'
import VideoDownloaderSEO from '../components/ui/VideoDownloaderSEO.jsx'
import { SITE_URL } from '../utils/seo.js'
import { useLanguage } from '../context/LanguageContext.jsx'

const title       = 'Instagram Video Downloader — Free Reels & MP4 Download | FastAudio'
const description = 'Download Instagram Reels, posts and IGTV as MP4 video or MP3 audio — no watermark, no login needed, free. Works on iPhone, Android & PC.'
const canonical   = `${SITE_URL}/instagram-video-downloader`

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    { '@type': 'Question', name: 'Can I download Instagram Reels as MP4?',            acceptedAnswer: { '@type': 'Answer', text: 'Yes. FastAudio downloads public Instagram Reels as MP4 video at up to 720p HD quality.' } },
    { '@type': 'Question', name: 'Is the Instagram downloader free?',                 acceptedAnswer: { '@type': 'Answer', text: 'Yes, completely free with no registration required.' } },
    { '@type': 'Question', name: 'Does it download without watermark?',               acceptedAnswer: { '@type': 'Answer', text: 'Yes. FastAudio fetches the original video from Instagram\'s CDN — no watermark or username overlay.' } },
    { '@type': 'Question', name: 'Can I download Instagram Stories?',                 acceptedAnswer: { '@type': 'Answer', text: 'No. Stories expire after 24 hours and require login to view. Only permanent public posts are supported.' } },
    { '@type': 'Question', name: 'What quality are downloaded Instagram Reels?',      acceptedAnswer: { '@type': 'Answer', text: 'Instagram Reels are typically stored at 720p HD. FastAudio downloads the best available resolution from Instagram\'s servers.' } },
  ],
}

const howToSchema = {
  '@context': 'https://schema.org',
  '@type': 'HowTo',
  name: 'How to Download Instagram Reels as MP4',
  step: [
    { '@type': 'HowToStep', name: 'Find the Instagram post',  text: 'Open the Reel or video post on Instagram.' },
    { '@type': 'HowToStep', name: 'Copy the link',            text: 'Tap ⋯ on the post → Copy Link.' },
    { '@type': 'HowToStep', name: 'Paste into FastAudio',     text: 'Paste the URL into the FastAudio search box.' },
    { '@type': 'HowToStep', name: 'Select MP4 or MP3',        text: 'Choose video (720p HD) or audio quality.' },
    { '@type': 'HowToStep', name: 'Click Download',           text: 'Hit ⚡ Download — the file saves to your device.' },
  ],
}

export default function InstagramVideoDownloader() {
  const { t } = useLanguage()
  return (
    <>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={canonical} />
        <meta property="og:title"       content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url"         content={canonical} />
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(howToSchema)}</script>
      </Helmet>
      <HomeTemplate
        provider="instagram"
        title={title}
        description={description}
        canonical={canonical}
        badge="Instagram Video Downloader"
        heading={<>Instagram Reels <span className="text-gradient">Downloader</span></>}
        subheading="Download Instagram Reels, feed videos and IGTV as MP4 (720p HD) or MP3 audio — no watermark, no Instagram account needed."
        placeholder={t('igPlaceholder')}
      >
        <VideoDownloaderSEO provider="instagram" />
      </HomeTemplate>
    </>
  )
}
